import { Box, Stack } from '@mui/material'
import StatusBadge from '../StatusBadge'
import SchemaTable from '../table/SchemaTable'
import TableButton from '../table/TableButton'
import FormattedDate from '../table/FormattedDate'
import { formatViewingDuration } from '../../data-organizers/announcements/formatViewingDuration'
import { formatFrequency } from '../../data-organizers/announcements/frequencies'

export function NotesCell({ notes }) {
    return (
        <Box
            title={notes ?? ''}
            sx={{
                maxWidth: '20rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}
        >
            {notes}
        </Box>
    )
}

function ScheduleCell({ startAt, endAt }) {
    if (startAt == null && endAt == null) {
        return <Box component="span">ללא תזמון</Box>
    }

    return (
        <Box component="span">
            {startAt != null ? <FormattedDate value={startAt} /> : '—'}
            {' – '}
            {endAt != null ? <FormattedDate value={endAt} /> : '—'}
        </Box>
    )
}

const nameColumn = {
    Header: 'שם',
    accessor: 'name',
}

const notesColumn = {
    Header: 'הערות',
    accessor: 'notes',
    enableSorting: false,
    Cell: (cell) => <NotesCell notes={cell.row.original.notes} />,
}

const statusColumn = {
    Header: 'סטטוס',
    accessor: 'isPublished',
    width: '6rem',
    Cell: (cell) => (
        <StatusBadge status={cell.row.original.isPublished ? 'published' : 'draft'} />
    ),
}

const frequencyColumn = {
    Header: 'תדירות',
    accessor: 'frequency',
    Cell: (cell) => formatFrequency(cell.row.original.frequency),
}

const scheduleColumn = {
    Header: 'תזמון',
    accessor: 'startAt',
    Cell: (cell) => (
        <ScheduleCell startAt={cell.row.original.startAt} endAt={cell.row.original.endAt} />
    ),
}

const viewingsColumn = {
    Header: 'צפיות',
    accessor: 'totalViewings',
    align: 'center',
    width: '5rem',
}

const viewingTimeColumn = {
    Header: 'זמן צפייה',
    accessor: 'totalViewingDuration',
    align: 'center',
    width: '7rem',
    Cell: (cell) => formatViewingDuration(cell.row.original.totalViewingDuration),
}

const conversionsColumn = {
    Header: 'פניות',
    accessor: 'totalConversions',
    align: 'center',
    width: '5rem',
}

const convertersColumn = {
    Header: 'פונים',
    accessor: 'totalUniqueConversions',
    align: 'center',
    width: '5rem',
}

export function RowActions({ row, onEdit, onPublish, onUnpublish, onClone, onPreview, onStats }) {
    const act = (handler) => (event) => {
        event.stopPropagation()
        handler?.(row)
    }

    return (
        <Stack direction="row" spacing={1}>
            <TableButton onClick={act(onEdit)}>עריכה</TableButton>
            <TableButton
                color="warning"
                onClick={act(row.isPublished ? onUnpublish : onPublish)}
            >
                {row.isPublished ? 'ביטול פרסום' : 'פרסום'}
            </TableButton>
            <TableButton onClick={act(onClone)}>שכפול</TableButton>
            <TableButton onClick={act(onPreview)}>תצוגה מקדימה</TableButton>
            <TableButton onClick={act(onStats)}>נתונים</TableButton>
        </Stack>
    )
}

export default function AnnouncementsTable({
    rows,
    onEdit,
    onPublish,
    onUnpublish,
    onClone,
    onPreview,
    onStats,
}) {
    const actionsColumn = {
        Header: 'פעולות',
        accessor: 'id',
        enableSorting: false,
        Cell: (cell) => (
            <RowActions
                row={cell.row.original}
                onEdit={onEdit}
                onPublish={onPublish}
                onUnpublish={onUnpublish}
                onClone={onClone}
                onPreview={onPreview}
                onStats={onStats}
            />
        ),
    }

    const columns = [
        nameColumn,
        notesColumn,
        statusColumn,
        frequencyColumn,
        scheduleColumn,
        viewingsColumn,
        viewingTimeColumn,
        conversionsColumn,
        convertersColumn,
        actionsColumn,
    ]

    return (
        <SchemaTable
            data={rows ?? []}
            columns={columns}
            onRowClick={(event, clicked) => onEdit?.(clicked.original)}
        />
    )
}
